#!/usr/bin/env node

import { execSync } from 'node:child_process';
import fs from 'node:fs/promises';
import path from 'node:path';
import { fileURLToPath } from 'node:url';

import { select as list } from '@inquirer/prompts';
import Spinnies from 'spinnies';

import { CONSOLE_ICONS } from '../constants/common';
import { track, TRACKING_EVENT, type TrackingProperties } from '../helpers/tracking';
import {
  assessPrerequisites,
  findPackages,
  findProjectRoot,
  getCodemodConfig,
  getOptions,
  loadTransformModules,
  logToInquirer,
  runTransformPrompts,
  validateClaudeConfig,
} from './helpers';
import { handleClaudePrereqs } from './helpers/claudePrereqs';

let isDebug = false;
const currentFilePath = fileURLToPath(import.meta.url);
const currentDirPath = path.dirname(currentFilePath);

const resetReportFile = async (reportPath: string) => {
  try {
    await fs.access(reportPath);
    await fs.rm(reportPath);
    console.debug(
      `${CONSOLE_ICONS.info} Removed existing report file${isDebug ? `: ${reportPath}` : '.'}`,
    );
  } catch {
    console.debug(
      `${CONSOLE_ICONS.info} No existing report file to remove${isDebug ? `: ${reportPath}` : '.'}`,
    );
  }
};

const summariseReportFile = async (reportPath: string) => {
  try {
    const reportContent = await fs.readFile(reportPath, 'utf8');
    const lines = reportContent.split('\n').filter(Boolean);
    if (lines.length) {
      console.debug(
        `\n${CONSOLE_ICONS.warning}  ${lines.length} manual review${lines.length > 1 ? 's are' : ' is'} required. See ${reportPath} for details.`,
      );
    } else {
      console.debug(
        `${CONSOLE_ICONS.info} Report file exists but is empty${isDebug ? `: ${reportPath}` : '.'}`,
      );
    }
  } catch {
    console.debug(`${CONSOLE_ICONS.info} No report file generated - no manual reviews needed`);
  }
};

const log = (label: string, value?: string): void => {
  if (typeof logToInquirer === 'function') {
    logToInquirer(label, value || '');
  } else {
    console.info(label, value || '');
  }
};

async function runCodemod(transformsDir?: string) {
  const args = process.argv.slice(2);
  const candidate = args[0];
  isDebug = args.includes('--debug');
  const startTime = Date.now();
  let repository = 'unknown';
  let trackingProps: Omit<TrackingProperties, 'stage' | 'durationMs'> = {
    transform: candidate,
    repository,
  };

  try {
    const root = findProjectRoot();
    repository = path.basename(root);
    const packagesPromise = findPackages();
    const resolvedTransformsDir =
      transformsDir ?? path.resolve(currentDirPath, '../dist/transforms');

    if (isDebug) {
      console.debug(
        `${CONSOLE_ICONS.info} Resolved transforms directory: ${resolvedTransformsDir}`,
      );
    }

    const { transformFiles: resolvedTransformNames } =
      await loadTransformModules(resolvedTransformsDir);
    if (resolvedTransformNames.length === 0) {
      throw new Error(
        `${CONSOLE_ICONS.error} No transform scripts found${isDebug ? ` in: ${resolvedTransformsDir}` : '.'}`,
      );
    }

    let transformFile: string;

    if (candidate && resolvedTransformNames.includes(candidate)) {
      log('Select codemod to run:', candidate);
      transformFile = candidate;
    } else {
      transformFile = await list({
        message: 'Select codemod to run:',
        choices: resolvedTransformNames.map((name: string) => ({ name, value: name })),
      });
      log('Selected codemod:', transformFile);
    }

    const codemodPath = path.resolve(resolvedTransformsDir, transformFile, 'transformer.js');
    const codemodConfig = getCodemodConfig(codemodPath);
    if (isDebug) {
      console.debug(`${CONSOLE_ICONS.info} Resolved codemod path: ${codemodPath}`);
    }

    if (codemodConfig?.type === 'claude') {
      validateClaudeConfig();
    }

    const spinners = new Spinnies();
    spinners.add('loading-codemod', { text: `Loading codemod (${transformFile})...` });
    const packages = await packagesPromise;
    const reportPath = path.resolve(root, 'codemod-report.txt');
    spinners.succeed('loading-codemod', {
      text: `Successfully loaded codemod: \x1b[2m${transformFile}\x1b[0m`,
    });

    if (codemodConfig?.type === 'jscodeshift') {
      await resetReportFile(reportPath);
    }

    const promptAnswers = await runTransformPrompts(codemodPath);
    const options = await getOptions({
      packages,
      root,
      transformFiles: resolvedTransformNames,
      preselectedTransformFile: transformFile,
      transformerType: codemodConfig?.type,
    });

    trackingProps = {
      transform: transformFile,
      engine: codemodConfig?.type,
      repository,
    };

    // Handle Claude transforms differently, as they work on multiple targets at once
    if (codemodConfig?.type === 'claude') {
      handleClaudePrereqs({ options, codemodPath, spinners });

      // Dynamically import the transformer module
      const transformerModule = (await import(codemodPath)) as {
        default: (targetPaths: string[], isDebug?: boolean) => Promise<void>;
      };
      const transformer = transformerModule.default;
      await transformer(options.targetPaths, isDebug);
    } else {
      // Button codemod doesn't use spinnies
      spinners.stopAll('succeed');
      spinners.checkIfActiveSpinners();

      let runAny = false;
      await Promise.all(
        options.targetPaths.map(async (targetPath) => {
          console.info(
            `${CONSOLE_ICONS.focus} \x1b[1mProcessing:\x1b[0m \x1b[32m${targetPath}\x1b[0m`,
          );

          // Check prerequisites for this target before running
          const ok = assessPrerequisites(targetPath, codemodPath);
          if (!ok) {
            return;
          }

          const answerArgs = Object.entries(promptAnswers).map(
            ([promptName, answerValue]) => `--${promptName}=${String(answerValue)}`,
          );

          const argsList = [
            '-t',
            codemodPath,
            targetPath,
            options.isDry ? '--dry' : '',
            options.isPrint ? '--print' : '',
            options.ignorePatterns
              ? options.ignorePatterns
                  .split(',')
                  .map((pattern) => `--ignore-pattern=${pattern.trim()}`)
                  .join(' ')
              : '',
            options.useGitIgnore ? '--gitignore' : '',
            ...answerArgs,
          ].filter(Boolean);
          const command = `npx jscodeshift ${argsList.join(' ')}`;

          if (isDebug) {
            console.debug(`${CONSOLE_ICONS.info} Running: ${command}`);
          }
          runAny = true;
          return execSync(command, { stdio: 'inherit' });
        }),
      );
      if (!runAny) {
        throw new Error('No valid target paths remaining after prerequisite checks');
      }
    }

    if (codemodConfig?.type === 'jscodeshift') {
      await summariseReportFile(reportPath);
    }

    track(
      TRACKING_EVENT,
      {
        ...trackingProps,
        stage: 'Success',
        durationMs: Date.now() - startTime,
      },
      isDebug,
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(`${CONSOLE_ICONS.error} Error running ${candidate} codemod:`, error.message);
    } else {
      console.error(`${CONSOLE_ICONS.error} Error running ${candidate} codemod:`, String(error));
    }
    track(
      TRACKING_EVENT,
      {
        ...trackingProps,
        stage: 'Failed',
        error: error instanceof Error ? error.message : String(error),
        durationMs: Date.now() - startTime,
      },
      isDebug,
    );

    if (process.env.NODE_ENV !== 'test') {
      process.exit(1);
    }
  }
}

export { runCodemod };
